import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';

type EmploymentIntermediaryFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: EmploymentIntermediaryFilterValues) => void;
};

export type EmploymentIntermediaryFilterValues = {
  agency: Option<string> | null;
  year: Moment | null;
  quarter: Option<string> | null;
};

const optionsQuarter = [
  {
    value: '2',
    label: `06 Apr - 05 July`,
  },
  {
    value: '3',
    label: `06 July  - 05 Oct `,
  },
  {
    value: '4',
    label: `06 Oct  - 05 Jan `,
  },
  {
    value: '1',
    label: `06 Jan  - 05 Apr`,
  },
];

const EmploymentIntermediaryFilterModal: React.FC<
  EmploymentIntermediaryFilterModalProps
> = ({ isOpen, toggle, onFilter }: EmploymentIntermediaryFilterModalProps) => {
  const { messages } = useTypedIntl();
  const { isAgency } = useSelector((state: AppState) => state.appStore);

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const ableToClearFilter =
    watchValue && Object.keys(watchValue).some((key) => !!watchValue[key]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
    onFilter({ agency: null, year: null, quarter: null });
    toggle();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    onFilter(values);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={{
          year: moment(),
          quarter: {
            value: '4',
            label: `06 Oct  - 05 Jan `,
          },
        }}
      >
        {!isAgency && (
          <Form.Item label={messages['common.agency']} name="agency">
            <AppSelectSync
              type="agency"
              apiType="searchAgency"
              placeholder={messages['form.pleaseSelect']}
              onChangeValue={(value) => {
                form.setFieldsValue({
                  agency: value,
                });
              }}
            />
          </Form.Item>
        )}

        <Form.Item label={messages['common.year']} name="year">
          <AppDatePicker
            placeholder={messages['form.pleaseSelect']}
            onChange={(date: Moment) => {
              form.setFieldsValue({ year: date });
            }}
            format="YYYY"
            picker="year"
          />
        </Form.Item>

        <Form.Item label={messages['common.quarter']} name="quarter">
          <AppSelect
            options={optionsQuarter ?? []}
            placeholder={messages['form.pleaseSelect']}
            onChangeValue={(value: Option<string>) => {
              form.setFieldsValue({
                quarter: value,
              });
            }}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default EmploymentIntermediaryFilterModal;
